import { ScheduleSetting, ScheduleViewPayload, viewsByPosition } from '../../../store/ScheduleSettings';
import { arrayMove } from '../../../utils/helpers';
import { viewNameFromKey } from './viewKey';

export const MAX_TABS = 10;

export interface TabRow {
  viewKey: string;
  tabName: string;
}

export interface PickableView {
  viewKey: string;
  name: string;
}

export const MAX_NAME_LENGTH = 255;

export const viewNameIn = (views: PickableView[], viewKey: string): string =>
  views.find((view) => view.viewKey === viewKey)?.name ?? viewNameFromKey(viewKey);

export const initialTabs = (schedule: ScheduleSetting | null, viewKey: string, viewName: string): TabRow[] => {
  const stored = viewsByPosition(schedule?.views);
  if (stored.length === 0) return [{ viewKey, tabName: viewName }];

  return stored.map((view) => ({ viewKey: view.view_key, tabName: view.tab_name }));
};

export const viewKeysOf = (schedule: ScheduleSetting): string[] =>
  schedule.views?.length ? viewsByPosition(schedule.views).map((view) => view.view_key) : [schedule.view_key];

export const canAddTabs = (rows: TabRow[]): boolean => rows.length < MAX_TABS;

export const addTabs = (rows: TabRow[], picked: PickableView[]): TabRow[] => [
  ...rows,
  ...picked.map((view) => ({ viewKey: view.viewKey, tabName: view.name }))
];

// The last tab stays: a report with no view has nothing to send.
export const removeTab = (rows: TabRow[], index: number): TabRow[] =>
  rows.length <= 1 ? rows : rows.filter((_row, position) => position !== index);

export const moveTab = (rows: TabRow[], from: number, to: number): TabRow[] => {
  if (from === to || from < 0 || to < 0 || from >= rows.length || to >= rows.length) return rows;

  const next = [...rows];
  arrayMove(next, from, to);
  return next;
};

export const reorderTabs = (
  rows: TabRow[],
  drop: { source: { index: number }; destination?: { index: number } | null }
): TabRow[] => (drop.destination ? moveTab(rows, drop.source.index, drop.destination.index) : rows);

export const renameTab = (rows: TabRow[], index: number, tabName: string): TabRow[] =>
  rows.map((row, position) => (position === index ? { ...row, tabName } : row));

// An empty name falls back to the view's, which is also what the API would default it to.
export const toViewsPayload = (rows: TabRow[], viewNameOf: (viewKey: string) => string): ScheduleViewPayload[] =>
  rows.map((row) => ({ view_key: row.viewKey, tab_name: row.tabName.trim() || viewNameOf(row.viewKey) }));
